import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import {
  type AgsReportDataResponse, type ReportDataRequest,
} from '~/exportReport/exportReport.repository';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import type {
  ReportTemplate, ReportTemplateDataRow,
} from '~/exportReport/template/reportTemplate';
import { createRadiusFilterItemRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import type { DemographicsQueryBulkRequestItem } from '~/store/demographics/demographics.repository';

export type ReportDemographicData = {
  id: string;
  label: string;
  value: number | string |null;
};

export const normalizeReportDemographicId = (demographicId: string): string => demographicId.replace('%', '');

export const getFormulaRowsById = (template: ReportTemplate): ReadonlyMap<string, ReportTemplateDataRow> => {
  return new Map(template.items.flatMap(item => item.type === 'table'
    ? item.content.flatMap(row => row.type === 'data-row' && row.formula ? [[row.id, row] as const] : [])
    : []));
};

export const getReportTemplateDemographicIds = (template: ReportTemplate): ReadonlyArray<string> => {
  return [...template.items.reduce<Set<string>>((demographicIds, item) => {
    if (item.type !== 'table') {
      return demographicIds;
    }

    item.content.forEach(row => {
      if (row.type !== 'data-row') {
        return;
      }

      demographicIds.add(row.id);
    });

    return demographicIds;
  }, new Set())];
};

export const getReportDemographicDataFromResults = (
  data: AgsReportDataResponse | null,
  columnId: string,
  demographicId: string
): ReportDemographicData | null => {
  const columnData = data?.areas.find(area => area.label === columnId);
  if (!columnData) {
    return null;
  }

  const resultData = columnData.data.find(demographic => demographic.tag === demographicId);
  const numericalValue = Number(resultData?.value);

  return resultData ? {
    value: isNaN(numericalValue) ? resultData?.value.toString() : numericalValue,
    label: resultData.description,
    id: demographicId,
  } : null;
};

export const getReportDemographicLabelFromResults = (
  data: AgsReportDataResponse | null,
  demographicId: string
): string | null => {
  if (!data?.areas[0]?.data) {
    return null;
  }

  const demographic = data.areas[0].data.find(demographicData => normalizeReportDemographicId(demographicData.tag) === normalizeReportDemographicId(demographicId));
  return demographic?.description ?? null;
};

export const createAgsReportDataLocationRequest = ({
  center, varlistId,
}: {
  center: LatLng;
  varlistId: string;
}): ReportDataRequest => {
  return {
    lat: center.lat,
    lng: center.lng,
    radiiInMiles: [5, 10, 15],
    country: 'usa',
    varlistId,
  };
};

export const createLocationExportReportDemographicsQueries = ({
  boundaryGroupId, center, demographicIds, mapId, tableColumns,
}: {
  boundaryGroupId: number | null;
  center: LatLng;
  demographicIds: ReadonlyArray<string>;
  mapId: number;
  tableColumns: ReadonlyArray<ReportTableColumn>;
}): DemographicsQueryBulkRequestItem[] => tableColumns.map(column => ({
  id: column.id,
  map_id: mapId,
  demographics_ids: demographicIds,
  ...(boundaryGroupId ? { boundary_group_id: boundaryGroupId } : {}),
  ...(column.id.startsWith('radius') ? {
    radius_filter: {
      type: 'or',
      radiuses: [createRadiusFilterItemRequest({
        radius: Number(column.id.replace('radius', '')),
        center,
        unit: UnitSystem.imperial,
      })],
    },
  } : {}),
}));
